import React, { useEffect, useMemo, useRef, useState } from 'react';
import './PayPalCheckoutPage.css';
import { apiRequest } from '../services/api';

function formatUsd(value) {
  const numeric = Number(value || 0);

  return numeric.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

function PayPalCheckoutPage() {
  const buttonContainerRef = useRef(null);
  const buttonInstanceRef = useRef(null);
  const [config, setConfig] = useState(null);
  const [selectedCoins, setSelectedCoins] = useState(100);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [sdkReady, setSdkReady] = useState(false);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [capturingOrder, setCapturingOrder] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const packs = Array.isArray(config?.packs) ? config.packs : [];
  const selectedPack = useMemo(
    () => packs.find((pack) => Number(pack.coins) === Number(selectedCoins)) || packs[0] || null,
    [packs, selectedCoins]
  );

  useEffect(() => {
    let active = true;

    const loadConfig = async () => {
      setLoadingConfig(true);
      setError('');
      setMessage('');

      try {
        const data = await apiRequest('/users/me/paypal/config');

        if (!active) {
          return;
        }

        setConfig(data);

        if (Array.isArray(data?.packs) && data.packs.length > 0) {
          setSelectedCoins(Number(data.packs[0].coins));
        }
      } catch (err) {
        if (!active) {
          return;
        }

        setError(err.message || 'Failed to load PayPal checkout.');
      } finally {
        if (active) {
          setLoadingConfig(false);
        }
      }
    };

    loadConfig();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!config?.client_id) {
      return undefined;
    }

    if (window.paypal) {
      setSdkReady(true);
      return undefined;
    }

    let active = true;
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(
      config.client_id
    )}&currency=${encodeURIComponent(config.currency || 'USD')}&intent=capture&components=buttons`;
    script.async = true;
    script.onload = () => {
      if (active) {
        setSdkReady(true);
      }
    };
    script.onerror = () => {
      if (active) {
        setError('Failed to load the PayPal SDK.');
      }
    };

    document.body.appendChild(script);

    return () => {
      active = false;
    };
  }, [config?.client_id, config?.currency]);

  useEffect(() => {
    if (!sdkReady || !selectedPack || !buttonContainerRef.current || !window.paypal) {
      return undefined;
    }

    setError('');
    setMessage('');
    buttonContainerRef.current.innerHTML = '';

    const buttons = window.paypal.Buttons({
      style: {
        layout: 'vertical',
        label: 'paypal',
      },
      createOrder: async () => {
        setCreatingOrder(true);
        setError('');
        setMessage('');

        try {
          const data = await apiRequest('/users/me/paypal/orders', {
            method: 'POST',
            body: {
              coins: Number(selectedPack.coins),
            },
          });

          return data.order_id;
        } catch (err) {
          setError(err.message || 'Failed to create PayPal order.');
          throw err;
        } finally {
          setCreatingOrder(false);
        }
      },
      onApprove: async (data) => {
        setCapturingOrder(true);
        setError('');
        setMessage('');

        try {
          const result = await apiRequest(
            `/users/me/paypal/orders/${encodeURIComponent(data.orderID)}/capture`,
            {
              method: 'POST',
              body: {},
            }
          );

          const successMessage = `${Number(result.coins || selectedPack.coins)} coins added successfully.`;
          setMessage(successMessage);

          if (window.opener && !window.opener.closed) {
            window.opener.postMessage(
              {
                type: 'talkali-paypal-purchase-complete',
                user: result.user || null,
                coins: Number(result.coins || selectedPack.coins),
              },
              window.location.origin
            );
          }

          window.setTimeout(() => {
            window.close();
          }, 1200);
        } catch (err) {
          setError(err.message || 'Failed to capture PayPal payment.');
        } finally {
          setCapturingOrder(false);
        }
      },
      onCancel: () => {
        setMessage('Payment was cancelled.');
      },
      onError: (err) => {
        setError(err?.message || 'PayPal checkout failed.');
      },
    });

    buttonInstanceRef.current = buttons;
    buttons.render(buttonContainerRef.current).catch((err) => {
      setError(err?.message || 'Failed to render PayPal checkout.');
    });

    return () => {
      if (buttonInstanceRef.current && typeof buttonInstanceRef.current.close === 'function') {
        buttonInstanceRef.current.close();
      }
      buttonInstanceRef.current = null;

      if (buttonContainerRef.current) {
        buttonContainerRef.current.innerHTML = '';
      }
    };
  }, [sdkReady, selectedPack]);

  return (
    <main className="paypal-checkout-page">
      <section className="paypal-checkout-card">
        <span className="paypal-checkout-eyebrow">Secure checkout</span>

        <h1 className="paypal-checkout-title">Add coins with PayPal</h1>

        <p className="paypal-checkout-subtitle">
          Choose a coin pack and complete your purchase in PayPal.
        </p>

        {loadingConfig ? (
          <div className="paypal-checkout-loading">Loading PayPal checkout...</div>
        ) : (
          <>
            <div className="paypal-pack-list">
              {packs.map((pack) => {
                const active = Number(pack.coins) === Number(selectedPack?.coins);

                return (
                  <button
                    className={`paypal-pack-button${active ? ' active' : ''}`}
                    key={pack.coins}
                    type="button"
                    onClick={() => setSelectedCoins(Number(pack.coins))}
                    disabled={creatingOrder || capturingOrder}
                  >
                    <div className="paypal-pack-row">
                      <strong className="paypal-pack-coins">
                        {Number(pack.coins).toLocaleString('en-US')} coins
                      </strong>
                      <span className="paypal-pack-amount">{formatUsd(pack.amount_usd)}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="paypal-checkout-summary">
              You are purchasing{' '}
              <strong>{Number(selectedPack?.coins || 0).toLocaleString('en-US')} coins</strong>{' '}
              for <strong>{formatUsd(selectedPack?.amount_usd || 0)}</strong>.
            </div>

            {error ? <div className="paypal-checkout-error">{error}</div> : null}

            {message ? <div className="paypal-checkout-success">{message}</div> : null}

            <div className={`paypal-button-host${creatingOrder || capturingOrder ? ' busy' : ''}`}>
              {sdkReady ? <div ref={buttonContainerRef} /> : 'Loading PayPal buttons...'}
            </div>
          </>
        )}
      </section>
    </main>
  );
}

export default PayPalCheckoutPage;